import type React from "react";
import { useEffect, useState } from "react";
import type { Product, WhatsAppTemplate } from "@qbanq/shared";
import { api } from "../api/client";

export function WhatsAppTemplatesPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [productId, setProductId] = useState("");
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("en_US");
  const [purpose, setPurpose] = useState<"reengagement" | "general">("reengagement");
  const [bodySample, setBodySample] = useState("");
  const [params, setParams] = useState("{{customer_name}}");
  const [isDefault, setIsDefault] = useState(true);

  function load() {
    void api.get<Product[]>("/api/products").then((p) => {
      setProducts(p);
      if (!productId && p[0]) setProductId(p[0].id);
    });
    void api.get<WhatsAppTemplate[]>("/api/whatsapp-templates").then(setTemplates);
  }
  useEffect(load, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    const param_mapping = params
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    await api.post("/api/whatsapp-templates", {
      product_id: productId,
      name,
      language,
      purpose,
      body_sample: bodySample,
      param_mapping,
      is_default: isDefault,
    });
    setName("");
    setBodySample("");
    load();
  }

  async function remove(id: string) {
    await api.del(`/api/whatsapp-templates/${id}`);
    load();
  }

  return (
    <div>
      <h2>WhatsApp Templates</h2>
      <p className="muted">
        Outside the 24-hour customer-service window, free-form replies are rejected by Meta. The
        platform sends the product's default <strong>re-engagement</strong> template instead.
        Templates must be approved in Meta Business Manager first; register the approved name here.
      </p>

      <div className="card">
        <h3>Register template</h3>
        <form onSubmit={create}>
          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <label>Product</label>
              <select value={productId} onChange={(e) => setProductId(e.target.value)}>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ width: 160 }}>
              <label>Purpose</label>
              <select value={purpose} onChange={(e) => setPurpose(e.target.value as "reengagement" | "general")}>
                <option value="reengagement">reengagement</option>
                <option value="general">general</option>
              </select>
            </div>
          </div>
          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <label>Approved template name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="reengagement_update_v1" />
            </div>
            <div className="field" style={{ width: 160 }}>
              <label>Language</label>
              <input value={language} onChange={(e) => setLanguage(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label>Body sample (reference only)</label>
            <input value={bodySample} onChange={(e) => setBodySample(e.target.value)} placeholder="Hi {{1}}, we have an update on your request." />
          </div>
          <div className="field">
            <label>Body params (comma-separated; use {"{{customer_name}}"} to inject the name)</label>
            <input value={params} onChange={(e) => setParams(e.target.value)} />
          </div>
          <label className="row" style={{ gap: 6 }}>
            <input type="checkbox" style={{ width: "auto" }} checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
            <span>Default re-engagement template for this product</span>
          </label>
          <button className="btn" type="submit" style={{ marginTop: 10 }}>Register</button>
        </form>
      </div>

      <div className="card">
        <table>
          <thead>
            <tr><th>Name</th><th>Lang</th><th>Purpose</th><th>Default</th><th>Product</th><th></th></tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td className="muted">{t.language}</td>
                <td>{t.purpose}</td>
                <td>{t.is_default ? "✓" : ""}</td>
                <td>{products.find((p) => p.id === t.product_id)?.name ?? t.product_id}</td>
                <td><button className="btn danger" onClick={() => remove(t.id)}>Delete</button></td>
              </tr>
            ))}
            {templates.length === 0 && (
              <tr><td colSpan={6} className="muted">No templates registered.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
